import { useState, useCallback } from 'react';
import { getVendor } from '../config/vendorRegistry';
import { getInitialConfig } from './useConfigurationLogic.jsx';
import { customPricingFromQuoteRow, customMetricsFromQuoteRow } from '../utils/quoteLoad';
import { hasOverrides } from '../domain/PricingOverrides';
import { applyOverrides } from '../pricing/applyOverrides';
import { customPricesMatch } from '../pricing/customPricesMatch';
import { fetchExecQuotePricing } from '../utils/execQuotePricing';
import { CUSTOM_METRICS_ENABLED } from '../utils/customMetricsEnabled';

// Stable empty-object reference for the customPrices state mirror's useState
// initializer, so the very first render passes the same kind of stable
// reference into useConfigurationLogic's debounced-effect deps that
// execPricing.customPrices (memoized) provides on every render after.
export const EMPTY_CUSTOM_PRICES = {};

/**
 * Load / reset / dialog state, extracted verbatim from StorageConfiguratorApp
 * (CONF-140 Phase 0b). The exec-pricing hook-order mirror and useSavedPricePin
 * stay in the component; this hook receives their setters.
 */
export function useConfigPersistence({ data, access, exec, config, ui }) {
  const { vendor, productDb } = data;
  const { hasSpecialAccess, hasExecAccess } = access;
  const { execPricing, execMetrics = null, pinSavedPrices, releasePin, setCustomPrices, loadedQuoteIdRef, execPricingTouched } = exec;
  const { loadConfig, switchVendor, setActiveView, updateConfig } = config;
  const { showToast, onLoaded } = ui;

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showQuotesModal, setShowQuotesModal] = useState(false);

  const clearSessionPricing = useCallback(() => {
    loadedQuoteIdRef.current = null;
    // Loading a clean quote clears any leftover session pricing (part-2 rule:
    // stale overrides must not bleed across quotes) AND releases any saved-price
    // pin left by a previously loaded custom-priced quote.
    releasePin();
    setCustomPrices(EMPTY_CUSTOM_PRICES);
    if (hasOverrides(execPricing.overrides)) execPricing.resetAll();
  }, [loadedQuoteIdRef, releasePin, setCustomPrices, execPricing]);

  // Load Config from the Saved quote (App.js:855–962, verbatim apart from the
  // SP price reflection being delegated to ui.onLoaded).
  // `_systemType` is accepted (not removed) purely for call-shape parity
  // with the classic call site — this hook derives systemType itself below
  // from savedConfig.systemType, so the argument is unused on purpose.
  const loadSavedConfig = useCallback((savedConfig, _systemType, quoteRow = null) => {
    // Sync vendor context if the saved config used a different vendor.
    // Potential timing issue — switchVendor triggers a React state update that
    // propagates on the next render, while loadConfig fires synchronously. React
    // batches both; if stale metrics are observed after a cross-vendor load,
    // investigate here first.
    if (savedConfig.hardwareVendor && savedConfig.hardwareVendor !== vendor.id) {
      switchVendor(savedConfig.hardwareVendor);
    }

    loadConfig(savedConfig);

    const loadedSystemType = savedConfig.systemType;
    if (loadedSystemType) {
      setActiveView(loadedSystemType === 'BaseSystem' ? 'BaseSystem' : 'Expansion');
    }

    if (hasSpecialAccess) {
      // Resolve against the LOADED vendor, not render-time state (switchVendor
      // above only takes effect next render).
      const loadedVendor = savedConfig.hardwareVendor ? (getVendor(savedConfig.hardwareVendor) || vendor) : vendor;
      onLoaded(savedConfig, loadedVendor);
    }

    // Part 3 (spec §2.2): custom-priced quotes pin displayed prices to the SAVED
    // outputs immediately — no derivation, no catalog fetch. The exec session
    // hydrates from the exec-only record in the background.
    const saved = quoteRow ? customPricingFromQuoteRow(quoteRow) : null;
    if (saved) {
      loadedQuoteIdRef.current = quoteRow.quoteId;
      if (hasOverrides(execPricing.overrides)) execPricing.resetAll();
      pinSavedPrices(saved.customPrices);
      if (hasExecAccess) {
        execPricingTouched.current = true;
        fetchExecQuotePricing(quoteRow.quoteId).then((rec) => {
          if (loadedQuoteIdRef.current !== quoteRow.quoteId) return;   // staleness guard
          if (!rec) {
            showToast({
              badge: 'EXEC PRICING', title: 'Custom pricing session unavailable',
              message: 'Couldn\'t load the custom-pricing session — prices shown as saved.',
            });
            return;
          }
          // Integrity check (spec §2.3) BEFORE hydrating: a mismatch never hydrates,
          // so the saved pin (set above) sticks.
          const derived = applyOverrides(rec.basisSnapshot, rec.overrides).customPriceDollars;
          if (!customPricesMatch(derived, saved.customPrices)) {
            console.warn('Custom-pricing integrity mismatch', { derived, saved: saved.customPrices });
            showToast({
              badge: 'EXEC PRICING', title: 'Pricing mismatch detected',
              message: 'Saved custom prices differ from recomputation — showing saved prices.',
            });
            return;
          }
          execPricing.hydrate({
            snapshot: rec.basisSnapshot, overrides: rec.overrides,
            overrideSetId: rec.overrideSetId, vendor: rec.vendor, quarter: rec.quarter,
          });
        });
      }
    } else {
      clearSessionPricing();
    }

    // CONF-212: saved metric overrides are output data on the row and must
    // display for every user, exec or not — the quote shows what the customer
    // saw. Clean loads clear the session set.
    const savedMetrics = CUSTOM_METRICS_ENABLED && quoteRow ? customMetricsFromQuoteRow(quoteRow) : null;
    if (savedMetrics) execMetrics?.hydrate(savedMetrics.overrides);
    else execMetrics?.resetAll();
  }, [vendor, switchVendor, loadConfig, setActiveView, hasSpecialAccess, hasExecAccess, onLoaded,
    execPricing, execMetrics, pinSavedPrices, loadedQuoteIdRef, execPricingTouched, showToast, clearSessionPricing]);

  /**
   * New (the V2 sidebar's "New System" / "New Expansion"): back to the vendor's
   * initial config with a clean session, as a base system or as an expansion.
   * The classic shell has no "New" entry point and never calls this.
   *
   * An expansion is the initial config put through the ordinary switch to
   * Expansion, so rules/transitions.ts empties it (no directors, both storage
   * sets off) exactly as it always has. That switch is QUEUED behind the load:
   * the transition is skipped while a load is in flight (it must not empty a
   * saved expansion quote as it loads), and loadConfig only ends its loading
   * window on a 0ms timer. Timers with equal delays fire in the order they were
   * set, so this one runs straight after it. Run inside the window instead, the
   * result is an "expansion" still carrying three directors and both sets —
   * useConfigPersistence.test.jsx pins this against the real engine.
   */
  const resetConfig = useCallback((systemType = 'BaseSystem') => {
    loadConfig(getInitialConfig(productDb, vendor.id));
    setActiveView(systemType);
    clearSessionPricing();
    execMetrics?.resetAll();
    if (systemType === 'Expansion') setTimeout(() => updateConfig({ systemType: 'Expansion' }), 0);
  }, [loadConfig, updateConfig, productDb, vendor.id, setActiveView, clearSessionPricing, execMetrics]);

  return { loadSavedConfig, resetConfig, showSaveDialog, setShowSaveDialog, showQuotesModal, setShowQuotesModal };
}

export default useConfigPersistence;
